import axios from '@/utils/axios'
import { wait } from '@/utils/wait'

export type DownloadParams = { shouldOpenNewTab?: boolean }

/**
 * Enqueue a file url to download.
 *
 * When loading a new url in the current tab, the browser will wait to see the content
 * headers to decide whether it should prompt to save or start a new page session, so the
 * downloads **need** to run in sequence to ensure that we don't discard pending requests
 * by overriding the url.
 *
 * @throws {Error}
 */
export function push(url: URL, fileName: string, downloadParams: DownloadParams = {}) {
  return DownloadQueue.push(async () => {
    const blob = await getBlob(url)
    const blobUrl = URL.createObjectURL(blob)
    await download(blobUrl, fileName, downloadParams)
    URL.revokeObjectURL(blobUrl)
  })
}

/**
 * A queue that runs async functions in sequence.
 */
class AsyncQueue {
  promise = Promise.resolve()

  /**
   * @param {() => void} fun An async function
   * @returns {Promise<void>} A promise that resolves after `fun` is triggered
   */
  push(fun: () => void) {
    return new Promise((resolve, reject) => {
      // Note: we need to ensure that the internal promise is never invalidated by errors
      // in the queued functions, because all subsequent steps of a promise are ignored
      // after one step fails.
      this.promise = this.promise
        .then(fun)
        .then(resolve)
        .catch(reject)
    })
  }
}

const DownloadQueue = new AsyncQueue()

/**
 * Get file from url as blob.
 *
 */
async function getBlob(url: URL): Promise<Blob> {
  return (await axios.get(url.toString(), { responseType: 'blob' })).data
}

/**
 * Download a file in the browser.
 */
export async function download(href: string, fileName: string, { shouldOpenNewTab }: DownloadParams = {}) {
  const link = document.createElement('a')
  link.href = href
  link.download = fileName
  link.style.display = 'none'

  if (shouldOpenNewTab) {
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
  }

  document.body.appendChild(link)
  link.click()
  await wait(100)
  link.remove()
}
